import * as React from 'react';
import { cn } from '../../lib/cn';

interface RadioGroupProps {
  value: string;
  onValueChange: (value: string) => void;
  className?: string;
  children: React.ReactNode;
  name?: string;
  /**
   * Accessible name for the group (the `role="radiogroup"` wrapper) —
   * needed whenever the group isn't already named by a `<fieldset>`/
   * `<legend>` or an enclosing `<FormField label>` isn't associated to it
   * via `aria-labelledby` some other way. Pass one of the two.
   */
  'aria-label'?: string;
  'aria-labelledby'?: string;
  /**
   * Id(s) of the element(s) describing the whole group — e.g. a field's
   * `<HelpText>` — so assistive tech reads the hint when focus enters the
   * group, not just the option label.
   */
  'aria-describedby'?: string;
}

const RadioGroupContext = React.createContext<{
  value: string;
  onValueChange: (value: string) => void;
  name: string;
}>({ value: '', onValueChange: () => undefined, name: 'radio-group' });

function RadioGroup({
  value,
  onValueChange,
  className,
  children,
  name = 'radio-group',
  'aria-label': ariaLabel,
  'aria-labelledby': ariaLabelledby,
  'aria-describedby': ariaDescribedby,
}: RadioGroupProps) {
  return (
    <RadioGroupContext.Provider value={{ value, onValueChange, name }}>
      <div
        role="radiogroup"
        aria-label={ariaLabel}
        aria-labelledby={ariaLabelledby}
        aria-describedby={ariaDescribedby}
        className={cn('flex flex-col gap-0', className)}
      >
        {children}
      </div>
    </RadioGroupContext.Provider>
  );
}

interface RadioGroupItemProps extends Omit<
  React.InputHTMLAttributes<HTMLInputElement>,
  'type' | 'checked' | 'onChange' | 'name' | 'value'
> {
  value: string;
}

const RadioGroupItem = React.forwardRef<HTMLInputElement, RadioGroupItemProps>(
  ({ value, className, ...props }, ref) => {
    const ctx = React.useContext(RadioGroupContext);
    const is_checked = ctx.value === value;
    return (
      <input
        ref={ref}
        type="radio"
        name={ctx.name}
        value={value}
        checked={is_checked}
        onChange={() => {
          ctx.onValueChange(value);
        }}
        className={cn(
          // .nv-radio: appearance-none resets native UI so the token's
          // border/background/size/border-radius take full effect.
          // The checked state is applied via the .is-checked modifier.
          'nv-radio appearance-none mt-0.5 shrink-0 cursor-pointer',
          is_checked && 'is-checked',
          className,
        )}
        {...props}
      />
    );
  },
);
RadioGroupItem.displayName = 'RadioGroupItem';

export { RadioGroup, RadioGroupItem };
export type { RadioGroupProps, RadioGroupItemProps };
